import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  IconButton,
  Grid,
  FormControlLabel,
  Switch,
  Tooltip,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';

const generateId = () => Math.random().toString(36).slice(2, 11);

const emptyAttribute = () => ({
  id: generateId(),
  name: '',
  value: ''
});

const emptyElement = () => ({
  id: generateId(),
  path: '',
  value: '',
  appendMode: false,
  cdata: false,
  attributes: [emptyAttribute()]
});

const normalizeObject = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeObject(item));
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeObject(value[key]);
        return acc;
      }, {});
  }
  return value;
};

const ActionsCreateXmlFile = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();

  const [targetPath, setTargetPath] = useState('');
  const [rootElement, setRootElement] = useState('Root');
  const [rootAttributes, setRootAttributes] = useState([emptyAttribute()]);
  const [namespaces, setNamespaces] = useState([]);
  const [elements, setElements] = useState([emptyElement()]);
  const [prettyPrint, setPrettyPrint] = useState(true);
  const [xmlDeclaration, setXmlDeclaration] = useState(true);
  const [encoding, setEncoding] = useState('utf-8');

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createxmlfile',
    onSaveStatusChange,
    customComparison: (initial) => {
      const buildPayload = () => preparePayload(rootElement, targetPath, rootAttributes, namespaces, elements, {
        prettyPrint,
        xmlDeclaration,
        encoding
      });
      const currentPayload = normalizeObject(buildPayload());
      const initialPayload = normalizeObject(preparePayload(
        initial.rootElement || '',
        initial.target || '',
        initial.rootAttributes || {},
        initial.namespaces || {},
        Array.isArray(initial.elements) ? initial.elements : [],
        {
          prettyPrint: initial.prettyPrint !== undefined ? initial.prettyPrint : true,
          xmlDeclaration: initial.xmlDeclaration !== undefined ? initial.xmlDeclaration : true,
          encoding: initial.encoding || 'utf-8'
        }
      ));
      return JSON.stringify(currentPayload) === JSON.stringify(initialPayload);
    },
    customSaveData: () => preparePayload(rootElement, targetPath, rootAttributes, namespaces, elements, {
      prettyPrint,
      xmlDeclaration,
      encoding
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setTargetPath(customData.target || '');
      setRootElement(customData.rootElement || 'Root');

      const parsedRootAttributes = dictToAttributeArray(customData.rootAttributes);
      setRootAttributes(
        parsedRootAttributes.length > 0
          ? parsedRootAttributes.map((attr) => ({ ...attr, id: generateId() }))
          : [emptyAttribute()]
      );

      const parsedNamespaces = dictToAttributeArray(customData.namespaces);
      setNamespaces(
        parsedNamespaces.map((ns) => ({
          id: generateId(),
          name: ns.name,
          value: ns.value
        }))
      );

      const loadedElements = Array.isArray(customData.elements) && customData.elements.length > 0
        ? customData.elements.map((element) => ({
            id: generateId(),
            path: element.path || '',
            value: element.value || '',
            appendMode: Boolean(element.appendMode),
            cdata: Boolean(element.cdata),
            attributes: (() => {
              const parsedAttributes = dictToAttributeArray(element.attributes);
              return parsedAttributes.length > 0
                ? parsedAttributes.map((attr) => ({
                    ...attr,
                    id: generateId()
                  }))
                : [emptyAttribute()];
            })()
          }))
        : [emptyElement()];

      setElements(loadedElements);
      setPrettyPrint(customData.prettyPrint !== undefined ? Boolean(customData.prettyPrint) : true);
      setXmlDeclaration(customData.xmlDeclaration !== undefined ? Boolean(customData.xmlDeclaration) : true);
      setEncoding(customData.encoding || 'utf-8');
    }
  });

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    ws: null,
    onFolderSelected: (folderPath) => {
      if (!folderPath) {
        return;
      }
      const cleanFolder = folderPath.replace(/[\\/]+$/, '');
      const separator = cleanFolder.includes('\\') ? '\\' : '/';
      const currentFileName = targetPath.split(/[/\\]/).pop() || 'output.xml';
      setTargetPath(`${cleanFolder}${separator}${currentFileName}`);
    }
  });

  useEffect(() => {
    updateSaveDisabledState();
  }, [
    targetPath,
    rootElement,
    rootAttributes,
    namespaces,
    elements,
    prettyPrint,
    xmlDeclaration,
    encoding,
    updateSaveDisabledState
  ]);

  const handleTargetChange = (event) => {
    setTargetPath(event.target.value);
  };

  const handleRootAttributeChange = (id, field, value) => {
    setRootAttributes((prev) =>
      prev.map((attr) => (attr.id === id ? { ...attr, [field]: value } : attr))
    );
  };

  const handleNamespaceChange = (id, field, value) => {
    setNamespaces((prev) =>
      prev.map((ns) => (ns.id === id ? { ...ns, [field]: value } : ns))
    );
  };

  const handleElementChange = (id, field, value) => {
    setElements((prev) =>
      prev.map((element) => (element.id === id ? { ...element, [field]: value } : element))
    );
  };

  const handleElementAttributeChange = (elementId, attributeId, field, value) => {
    setElements((prev) =>
      prev.map((element) => {
        if (element.id !== elementId) {
          return element;
        }
        const updatedAttributes = element.attributes.map((attr) =>
          attr.id === attributeId ? { ...attr, [field]: value } : attr
        );
        return { ...element, attributes: updatedAttributes };
      })
    );
  };

  const addRootAttribute = () => {
    setRootAttributes((prev) => [...prev, emptyAttribute()]);
  };

  const removeRootAttribute = (id) => {
    setRootAttributes((prev) => {
      if (prev.length === 1) {
        return [emptyAttribute()];
      }
      return prev.filter((attr) => attr.id !== id);
    });
  };

  const addNamespace = () => {
    setNamespaces((prev) => [...prev, { id: generateId(), name: '', value: '' }]);
  };

  const removeNamespace = (id) => {
    setNamespaces((prev) => prev.filter((ns) => ns.id !== id));
  };

  const addElement = () => {
    setElements((prev) => [...prev, emptyElement()]);
  };

  const removeElement = (id) => {
    setElements((prev) => {
      if (prev.length === 1) {
        return [emptyElement()];
      }
      return prev.filter((element) => element.id !== id);
    });
  };

  const addElementAttribute = (elementId) => {
    setElements((prev) =>
      prev.map((element) =>
        element.id === elementId
          ? { ...element, attributes: [...element.attributes, emptyAttribute()] }
          : element
      )
    );
  };

  const removeElementAttribute = (elementId, attributeId) => {
    setElements((prev) =>
      prev.map((element) => {
        if (element.id !== elementId) {
          return element;
        }
        const remaining = element.attributes.filter((attr) => attr.id !== attributeId);
        return {
          ...element,
          attributes: remaining.length > 0 ? remaining : [emptyAttribute()]
        };
      })
    );
  };

  return (
    <ActionSettingsLayout
      title={t('createxmlfile', 'Create XML File')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={
              <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 'bold' }}>
                {t('workflow.create_xml_file.target_title', 'Target XML File')}
              </Typography>
            }
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TagEditor
                    caseid={caseid}
              value={targetPath}
              onChange={setTargetPath}
              placeholder="C:\\Exports\\{projectid}\\summary.xml"
              singleLine
              tags={tags}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={
              <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 'bold' }}>
                {t('workflow.create_xml_file.structure_title', 'XML Structure')}
              </Typography>
            }
            sx={{ p: { xs: 1.5, sm: 2 } }} 
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('workflow.create_xml_file.root_element', 'Root element')}
                  value={rootElement}
                  onChange={(event) => setRootElement(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={prettyPrint}
                      onChange={(event) => setPrettyPrint(event.target.checked)}
                    />
                  }
                  label={t('workflow.create_xml_file.pretty_print', 'Pretty print')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={xmlDeclaration}
                      onChange={(event) => setXmlDeclaration(event.target.checked)}
                    />
                  }
                  label={t('workflow.create_xml_file.include_declaration', 'Include declaration')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('workflow.create_xml_file.encoding', 'Encoding')}
                  value={encoding}
                  onChange={(event) => setEncoding(event.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 'bold' }}>
                {t('workflow.create_xml_file.root_attributes_title', 'Root attributes & namespaces')}
              </Typography>
            }
            sx={{ p: { xs: 1.5, sm: 2 } }}
            action={
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={addRootAttribute} startIcon={<AddIcon />} size="small">
                  {t('workflow.create_xml_file.add_attribute', 'Add Attribute')}
                </Button>
                <Button variant="outlined" onClick={addNamespace} startIcon={<AddIcon />} size="small">
                  {t('workflow.create_xml_file.add_namespace', 'Add Namespace')}
                </Button>
              </Stack>
            }
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Stack spacing={2}>
              {rootAttributes.map((attr) => (
                <Grid container spacing={2} alignItems="center" key={attr.id}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label={t('workflow.create_xml_file.attribute_name', 'Attribute name')}
                      value={attr.name}
                      onChange={(event) => handleRootAttributeChange(attr.id, 'name', event.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TagEditor
                    caseid={caseid}
                      value={attr.value}
                      onChange={(val) => handleRootAttributeChange(attr.id, 'value', val)}
                      placeholder="Attribute value"
                      singleLine
                      tags={tags}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <IconButton onClick={() => removeRootAttribute(attr.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              {namespaces.length > 0 && (
                <>
                  <Divider />
                  {namespaces.map((ns) => (
                    <Grid container spacing={2} alignItems="center" key={ns.id}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          label={t('workflow.create_xml_file.namespace_prefix', 'Namespace prefix')}
                          value={ns.name}
                          onChange={(event) => handleNamespaceChange(ns.id, 'name', event.target.value)}
                          placeholder="e.g. ns"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TagEditor
                    caseid={caseid}
                          value={ns.value}
                          onChange={(val) => handleNamespaceChange(ns.id, 'value', val)}
                          placeholder="https://example.com/schema"
                          singleLine
                          tags={tags}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <IconButton onClick={() => removeNamespace(ns.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 'bold' }}>
                {t('workflow.create_xml_file.elements_title', 'Elements')}
              </Typography>
            }
            sx={{ p: { xs: 1.5, sm: 2 } }}
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={addElement} size="small">
                {t('workflow.create_xml_file.add_element', 'Add Element')}
              </Button>
            }
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Stack spacing={3}>
              {elements.map((element) => (
                <Card key={element.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                          <TextField
                            label={t('workflow.create_xml_file.element_path', 'Element path')}
                            value={element.path}
                            onChange={(event) => handleElementChange(element.id, 'path', event.target.value)}
                            fullWidth
                            placeholder="Patient/Name/First"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
                        >
                          <Tooltip title={t('workflow.create_xml_file.remove_element', 'Remove element')}>
                            <span>
                              <Button
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                onClick={() => removeElement(element.id)}
                                disabled={elements.length === 1}
                              >
                                {t('remove', 'Remove')}
                              </Button>
                            </span>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                          <TagEditor
                    caseid={caseid}
                             value={element.value}
                             onChange={(val) => handleElementChange(element.id, 'value', val)}
                             multiline
                             minRows={2}
                             tags={tags}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={element.appendMode}
                                onChange={(event) => handleElementChange(element.id, 'appendMode', event.target.checked)}
                              />
                            }
                            label={t('workflow.create_xml_file.append_mode', 'Append mode')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={element.cdata}
                                onChange={(event) => handleElementChange(element.id, 'cdata', event.target.checked)}
                              />
                            }
                            label={t('workflow.create_xml_file.use_cdata', 'Wrap value in CDATA')}
                          />
                        </Grid>
                      </Grid>

                      <Divider />

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => addElementAttribute(element.id)}
                        >
                          {t('workflow.create_xml_file.add_element_attribute', 'Add Attribute')}
                        </Button>
                      </Stack>

                      <Stack spacing={1}>
                        {element.attributes.map((attr) => (
                          <Grid container spacing={2} alignItems="center" key={attr.id}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label={t('workflow.create_xml_file.attribute_name', 'Attribute name')}
                                value={attr.name}
                                onChange={(event) =>
                                  handleElementAttributeChange(element.id, attr.id, 'name', event.target.value)
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TagEditor
                    caseid={caseid}
                                value={attr.value}
                                onChange={(val) =>
                                  handleElementAttributeChange(element.id, attr.id, 'value', val)
                                }
                                singleLine
                                tags={tags}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Stack direction="row" justifyContent="flex-end">
                                <IconButton onClick={() => removeElementAttribute(element.id, attr.id)} size="small" color="error">
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            </Grid>
                          </Grid>
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

const dictToAttributeArray = (input, nameKey = 'name', valueKey = 'value') => {
  if (!input) {
    return [];
  }

  if (Array.isArray(input)) {
    return input.map((item) => ({
      [nameKey]: item[nameKey] ?? item.name ?? '',
      [valueKey]: item[valueKey] ?? item.value ?? ''
    }));
  }

  if (typeof input === 'object') {
    return Object.keys(input).map((key) => ({
      [nameKey]: key,
      [valueKey]: input[key]
    }));
  }

  return [];
};

const ensureAttributeArray = (input) => {
  if (!input) {
    return [];
  }
  if (Array.isArray(input)) {
    return input;
  }
  if (typeof input === 'object') {
    return dictToAttributeArray(input);
  }
  return [];
};

const preparePayload = (rootElement, targetPath, rootAttributes, namespaces, elements, options) => {
  const serializeAttributes = (source) =>
    ensureAttributeArray(source)
      .filter((attr) => attr.name && attr.name.trim() !== '')
      .reduce((acc, attr) => {
        acc[attr.name.trim()] = attr.value ?? '';
        return acc;
      }, {});

  const serializedElements = (elements || [])
    .filter((element) => element.path && element.path.trim() !== '')
    .map((element) => ({
      path: element.path.trim(),
      value: element.value ?? '',
      appendMode: Boolean(element.appendMode),
      cdata: Boolean(element.cdata),
      attributes: serializeAttributes(element.attributes)
    }));

  const payload = {
    target: targetPath || '',
    rootElement: rootElement || '',
    rootAttributes: serializeAttributes(rootAttributes),
    namespaces: serializeAttributes(namespaces),
    elements: serializedElements,
    prettyPrint: Boolean(options.prettyPrint),
    xmlDeclaration: Boolean(options.xmlDeclaration),
    encoding: options.encoding || 'utf-8'
  };

  return payload;
};

export default React.memo(ActionsCreateXmlFile);
